/**
 * ManuscriptInventoryPanel — admin Kanban for purchased manuscript pipeline.
 *
 * Columns: Offered → Under Review → Acquired → Author Paid → Published
 * Each column transition fires a double-entry JE on the backend.
 * Financial summary bar shows live inventory value, AP owed, COGS recognised.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, BookOpen, DollarSign, Loader2, Check, ChevronRight,
  Edit2, Trash2, X, ArrowRight, TrendingDown, Package, AlertCircle,
  FileText, User, Tag,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent,
  AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

const API  = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });
const fmt  = (cents) => cents != null ? `$${(cents / 100).toFixed(2)}` : '—';

// ─── Column config ───────────────────────────────────────────────────────────
const COLUMNS = [
  { key: 'offered',       label: 'Offered',       color: 'bg-sky-500/10 border-sky-500/30 text-sky-800 dark:text-sky-300' },
  { key: 'under_review',  label: 'Under Review',  color: 'bg-amber-500/10 border-amber-500/30 text-amber-800 dark:text-amber-300' },
  { key: 'acquired',      label: 'Acquired',      color: 'bg-violet-500/10 border-violet-500/30 text-violet-800 dark:text-violet-300' },
  { key: 'author_paid',   label: 'Author Paid',   color: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-800 dark:text-emerald-300' },
  { key: 'published',     label: 'Published',     color: 'bg-primary/10 border-primary/30 text-primary' },
];

// ─── Create / Edit form ───────────────────────────────────────────────────────
function ManuscriptForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    title: '', author_name: '', genre_slug: '',
    word_count: '', acquisition_price_cents: '',
    synopsis: '', notes: '', cover_url: '',
    ...(initial || {}),
    acquisition_price_cents: initial?.acquisition_price_cents
      ? (initial.acquisition_price_cents / 100).toFixed(2) : '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = () => {
    if (!form.title || !form.author_name) {
      toast.error('Title and author name are required.');
      return;
    }
    const cents = form.acquisition_price_cents
      ? Math.round(parseFloat(form.acquisition_price_cents) * 100) : 0;
    onSave({ ...form, acquisition_price_cents: cents, word_count: parseInt(form.word_count, 10) || 0 });
  };

  const fields = [
    { key: 'title',       label: 'Title *',       placeholder: 'e.g. The Ivory Gate' },
    { key: 'author_name', label: 'Author Name *',  placeholder: 'e.g. Miriam Holt' },
    { key: 'genre_slug',  label: 'Genre Slug',     placeholder: 'e.g. fiction, sci-fi' },
    { key: 'cover_url',   label: 'Cover Image URL', placeholder: 'https://…/cover.jpg' },
  ];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        {fields.map(f => (
          <div key={f.key} className="space-y-1">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{f.label}</label>
            <input
              value={form[f.key] || ''}
              onChange={e => set(f.key, e.target.value)}
              placeholder={f.placeholder}
              className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[13px]"
              data-testid={`ms-form-${f.key}`}
            />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Word Count</label>
          <input type="number" value={form.word_count} onChange={e => set('word_count', e.target.value)}
            placeholder="80000" className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[13px]"
            data-testid="ms-form-word-count" />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Acquisition Price ($)</label>
          <input type="number" step="0.01" value={form.acquisition_price_cents} onChange={e => set('acquisition_price_cents', e.target.value)}
            placeholder="500.00" className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[13px]"
            data-testid="ms-form-price" />
        </div>
      </div>
      <div className="space-y-1">
        <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Synopsis</label>
        <textarea value={form.synopsis} onChange={e => set('synopsis', e.target.value)} rows={2}
          className="w-full resize-none rounded-md border border-border bg-background px-2 py-1.5 text-[13px]"
          data-testid="ms-form-synopsis" />
      </div>
      <div className="space-y-1">
        <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Internal Notes</label>
        <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2}
          className="w-full resize-none rounded-md border border-border bg-background px-2 py-1.5 text-[13px]"
          data-testid="ms-form-notes" />
      </div>
      <div className="flex gap-2 pt-1">
        <Button size="sm" onClick={handleSave} disabled={saving} data-testid="ms-form-save" className="gap-1">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          {initial ? 'Save changes' : 'Add manuscript'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
      </div>
    </div>
  );
}

// ─── Manuscript card ─────────────────────────────────────────────────────────
function ManuscriptCard({ ms, onEdit, onAdvance, advancing }) {
  const nextStatus = {
    offered:     'under_review',
    under_review: 'acquired',
    acquired:    'author_paid',
    author_paid: 'published',
  }[ms.status];

  const advanceLabels = {
    under_review: 'Move to Review',
    acquired:     'Acquire',
    author_paid:  'Mark Paid',
    published:    'Publish',
  };

  const [acquirePrice, setAcquirePrice] = useState('');
  const [showAcquireInput, setShowAcquireInput] = useState(false);

  return (
    <div className="rounded-lg border border-border bg-card p-3 space-y-2" data-testid={`ms-card-${ms.id}`}>
      {ms.cover_url && (
        <img src={ms.cover_url} alt={ms.title}
          className="w-full h-24 object-cover rounded-md"
          onError={e => { e.target.style.display = 'none'; }} />
      )}
      <div>
        <p className="text-[13px] font-semibold leading-tight line-clamp-2">{ms.title}</p>
        <p className="text-[11.5px] text-muted-foreground flex items-center gap-1 mt-0.5">
          <User className="h-3 w-3" /> {ms.author_name}
        </p>
      </div>
      {ms.genre_slug && (
        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 text-[10.5px] text-muted-foreground">
          <Tag className="h-2.5 w-2.5" /> {ms.genre_slug}
        </span>
      )}
      <div className="flex items-center justify-between text-[11.5px] text-muted-foreground">
        {ms.word_count > 0 && <span>{ms.word_count.toLocaleString()} words</span>}
        {ms.acquisition_price_cents > 0 && (
          <span className="font-semibold text-foreground">{fmt(ms.acquisition_price_cents)}</span>
        )}
      </div>
      {ms.synopsis && (
        <p className="text-[11.5px] text-muted-foreground line-clamp-2">{ms.synopsis}</p>
      )}
      {ms.je_ids?.length > 0 && (
        <div className="flex items-center gap-1 text-[10.5px] text-emerald-600">
          <Check className="h-3 w-3" /> {ms.je_ids.length} JE{ms.je_ids.length > 1 ? 's' : ''} posted
        </div>
      )}

      {/* Acquire price input */}
      {showAcquireInput && nextStatus === 'acquired' && (
        <div className="flex gap-1.5 items-center">
          <span className="text-[12px] text-muted-foreground">$</span>
          <input
            type="number" step="0.01" min="0"
            value={acquirePrice}
            onChange={e => setAcquirePrice(e.target.value)}
            placeholder="0.00"
            className="flex-1 rounded border border-border bg-background px-2 py-1 text-[12px]"
            data-testid={`ms-acquire-price-${ms.id}`}
          />
          <Button size="sm" className="h-6 text-[11px] px-2" onClick={() => {
            const cents = Math.round(parseFloat(acquirePrice) * 100);
            if (!cents) { toast.error('Enter a price.'); return; }
            onAdvance(ms.id, 'acquired', { acquisition_price_cents: cents });
            setShowAcquireInput(false);
          }}>Confirm</Button>
          <Button size="sm" variant="ghost" className="h-6 text-[11px] px-2"
            onClick={() => setShowAcquireInput(false)}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      <div className="flex gap-1.5 pt-0.5">
        <Button size="sm" variant="outline" onClick={() => onEdit(ms)}
          className="gap-1 h-6 text-[11px] px-2" data-testid={`ms-edit-${ms.id}`}>
          <Edit2 className="h-2.5 w-2.5" /> Edit
        </Button>
        {nextStatus && (
          <Button size="sm"
            onClick={() => {
              if (nextStatus === 'acquired') {
                setAcquirePrice((ms.acquisition_price_cents / 100).toFixed(2) || '');
                setShowAcquireInput(true);
              } else {
                onAdvance(ms.id, nextStatus, {});
              }
            }}
            disabled={advancing === ms.id || showAcquireInput}
            className="gap-1 h-6 text-[11px] px-2 flex-1"
            data-testid={`ms-advance-${ms.id}`}
          >
            {advancing === ms.id
              ? <Loader2 className="h-2.5 w-2.5 animate-spin" />
              : <ArrowRight className="h-2.5 w-2.5" />}
            {advanceLabels[nextStatus]}
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Summary bar ─────────────────────────────────────────────────────────────
function SummaryBar({ summary }) {
  if (!summary) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {[
        { label: 'Inventory (15100)', value: fmt(summary.inventory_value_cents), icon: Package, color: 'text-violet-600' },
        { label: 'AP Owed to Authors', value: fmt(summary.ap_owed_cents), icon: DollarSign, color: 'text-amber-600' },
        { label: 'Total Paid Out',     value: fmt(summary.total_paid_cents),  icon: Check, color: 'text-emerald-600' },
        { label: 'Published COGS',     value: fmt(summary.published_cogs_cents), icon: TrendingDown, color: 'text-rose-600' },
      ].map(({ label, value, icon: Icon, color }) => (
        <div key={label} className="rounded-lg border border-border bg-card p-3">
          <div className={cn('flex items-center gap-1.5 mb-1', color)}>
            <Icon className="h-3.5 w-3.5" />
            <span className="text-[11px] font-semibold uppercase tracking-wider">{label}</span>
          </div>
          <p className="text-[18px] font-bold">{value}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Main panel ──────────────────────────────────────────────────────────────
export default function ManuscriptInventoryPanel() {
  const [manuscripts, setManuscripts] = useState([]);
  const [summary,     setSummary]     = useState(null);
  const [creating,    setCreating]    = useState(false);
  const [editTarget,  setEditTarget]  = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [advancing,   setAdvancing]   = useState(null);
  const [loading,     setLoading]     = useState(true);

  const load = useCallback(async () => {
    try {
      const [mRes, sRes] = await Promise.all([
        http.get('/admin/manuscripts'),
        http.get('/admin/manuscripts/inventory-summary'),
      ]);
      setManuscripts(mRes.data.manuscripts || []);
      setSummary(sRes.data);
    } catch {
      toast.error('Could not load manuscript inventory.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (form) => {
    setSaving(true);
    try {
      await http.post('/admin/manuscripts', form);
      toast.success('Manuscript added.');
      setCreating(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (form) => {
    setSaving(true);
    try {
      await http.put(`/admin/manuscripts/${editTarget.id}`, form);
      toast.success('Manuscript updated.');
      setEditTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleAdvance = async (id, nextStatus, extra = {}) => {
    setAdvancing(id);
    try {
      const endpointMap = {
        acquired:    () => http.post(`/admin/manuscripts/${id}/acquire`, { acquisition_price_cents: extra.acquisition_price_cents }),
        author_paid: () => http.post(`/admin/manuscripts/${id}/pay-author`, {}),
        published:   () => http.post(`/admin/manuscripts/${id}/publish`),
        under_review: () => http.put(`/admin/manuscripts/${id}`, { status: 'under_review' }),
      };
      const fn = endpointMap[nextStatus];
      if (!fn) { toast.error('Unknown transition.'); return; }
      const r = await fn();
      const jeId = r.data?.je_id;
      toast.success(
        jeId
          ? `Moved to ${nextStatus.replace('_', ' ')} — JE posted (${jeId.slice(-6)}).`
          : `Moved to ${nextStatus.replace('_', ' ')}.`
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Transition failed.');
    } finally {
      setAdvancing(null);
    }
  };

  const grouped = COLUMNS.reduce((acc, col) => {
    acc[col.key] = manuscripts.filter(m => m.status === col.key);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4" data-testid="manuscript-inventory-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[15px] font-semibold flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-primary" /> Manuscript Inventory
          </h2>
          <p className="text-[12.5px] text-muted-foreground mt-0.5">
            Acquisition pipeline — each stage transition posts a GAAP journal entry.
          </p>
        </div>
        <Button size="sm" onClick={() => setCreating(true)} data-testid="ms-add-btn" className="gap-1">
          <Plus className="h-3.5 w-3.5" /> Add manuscript
        </Button>
      </div>

      {/* Financial summary */}
      <SummaryBar summary={summary} />

      {/* Create form */}
      {creating && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
          <h3 className="text-[13px] font-semibold mb-3 flex items-center gap-2">
            <FileText className="h-4 w-4" /> New Manuscript Offer
          </h3>
          <ManuscriptForm onSave={handleCreate} onCancel={() => setCreating(false)} saving={saving} />
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onOpenChange={v => !v && setEditTarget(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Manuscript — {editTarget?.title}</DialogTitle>
          </DialogHeader>
          {editTarget && (
            <ManuscriptForm
              initial={editTarget}
              onSave={handleEdit}
              onCancel={() => setEditTarget(null)}
              saving={saving}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Kanban board */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5 overflow-x-auto">
        {COLUMNS.map(col => (
          <div key={col.key} className="min-w-[160px]">
            <div className={cn(
              'mb-2 rounded-md border px-2.5 py-1.5 flex items-center justify-between',
              col.color,
            )}>
              <span className="text-[12px] font-semibold">{col.label}</span>
              <span className="text-[11px] opacity-70">{grouped[col.key]?.length || 0}</span>
            </div>
            <div className="space-y-2">
              {(grouped[col.key] || []).map(ms => (
                <ManuscriptCard
                  key={ms.id}
                  ms={ms}
                  onEdit={setEditTarget}
                  onAdvance={handleAdvance}
                  advancing={advancing}
                />
              ))}
              {(grouped[col.key] || []).length === 0 && (
                <div className="rounded-md border border-dashed border-border bg-muted/10 px-2 py-4 text-center">
                  <p className="text-[11px] text-muted-foreground/60">Empty</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
